import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import { CreditFacilityPrincipalUsesTable } from "features/credit-facility/CreditFacilityPrincipalUsesTable";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import { CreditFacilityMergeTranchesSummaryTables } from "features/credit-facility/merge-tranches/CreditFacilityMergeTranchesReview";
import { CreditFacilityReallocationSummaryTable } from "features/credit-facility/reallocation/CreditFacilityReallocationSummaryTable";
import {
  getCreditFacilityEventsTableDataRetrieveQueryKey,
  getCreditFacilityPrincipalUsesRetrieveQueryKey,
  getCreditFacilitySummaryRetrieveQueryKey,
} from "gen/endpoints/credit-facility/credit-facility";
import {
  useFundFamilyEventPendingToCompleteUpdate,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { FundFamilyEventSubTypeEnum } from "gen/model/fundFamilyEventSubTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import {
  PageWizardProvider,
  PageWizardRoutes,
} from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilityPrincipalEventPage: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "enums"]);
  const { creditFacilityId, fundFamilyId, fundFamilyEventId } =
    useRequiredStringParams();

  const { mutateAsync: updateEventToComplete } =
    useFundFamilyEventPendingToCompleteUpdate();

  const { data: fundFamilyEvent, refetch } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const backToSummary = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .creditFacilities({})
        .creditFacility({ creditFacilityId }).$,
    );
  }, [navigate, fundFamilyId, creditFacilityId]);

  const invalidateQueries = useCallback(async () => {
    await invalidateQueryKeys(
      queryClient,
      getCreditFacilityEventsTableDataRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
      getCreditFacilityPrincipalUsesRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
      getCreditFacilitySummaryRetrieveQueryKey(fundFamilyId, creditFacilityId),
    );
    backToSummary();
  }, [fundFamilyId, backToSummary, queryClient, creditFacilityId]);

  const finalizeEvent = useCallback(async () => {
    await updateEventToComplete({
      fundFamilyId,
      fundFamilyEventId,
    });
    await invalidateQueries();
    backToSummary();
  }, [
    updateEventToComplete,
    fundFamilyId,
    fundFamilyEventId,
    invalidateQueries,
    backToSummary,
  ]);

  const discardEvent = useCallback(async () => {
    await invalidateQueries();
    backToSummary();
  }, [invalidateQueries, backToSummary]);

  if (!fundFamilyEvent.id) {
    return null;
  }

  const pageProps: CreditFacilitySubpageProps = {
    fundFamilyId,
    creditFacilityId,
    fundFamilyEvent,
    refetchEvent: refetch,
  };

  if (fundFamilyEvent.state !== FundFamilyEventStateMachineFieldsEnum.draft) {
    return (
      <CreditFacilityEventLayout canEditEvent={false}>
        {!!fundFamilyEvent.out_of_date_reason && (
          <OutOfDateAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            message={fundFamilyEvent.out_of_date_reason}
            onDiscard={discardEvent}
          />
        )}
        {fundFamilyEvent.sub_type == FundFamilyEventSubTypeEnum.reallocation ? (
          <CreditFacilityReallocationSummaryTable />
        ) : fundFamilyEvent.sub_type ==
          FundFamilyEventSubTypeEnum.merge_tranches ? (
          <CreditFacilityMergeTranchesSummaryTables />
        ) : (
          <CreditFacilityPrincipalUsesTable
            fundFamilyEventId={fundFamilyEvent.id}
            eventType={fundFamilyEvent.sub_type}
            title={t("creditFacility:event_summary", {
              eventType: t(
                `enums:FundFamilyEventSubTypeEnum.${fundFamilyEvent.sub_type!}`,
              ),
            })}
          />
        )}
      </CreditFacilityEventLayout>
    );
  }

  return (
    <PageWizardProvider
      finishTitle={t("creditFacility:finalizeEvent", {
        eventType: t(
          `enums:FundFamilyEventSubTypeEnum.${fundFamilyEvent.sub_type!}`,
        ),
      })}
      onFinish={finalizeEvent}
      onExit={backToSummary}
    >
      <PageWizardRoutes context={pageProps} />
    </PageWizardProvider>
  );
};
